import React from 'react';
import './styles.css';

/**
 * Props for {@link Panel}.
 *
 * - className: extra classname to add to the wrapper div styling
 * - type: a class `panel-${type}` is added to the wrapper div to allow
 *   different styling
 * - header: function to render the header
 * - collapsed: whether or not the panel is collapsed
 * - collapsedHeader: function to render the header when the panel is collapsed
 */
interface PanelProps {
  className?: string;
  type: string;
  header: () => React.ReactNode;
  children?: React.ReactNode;
  collapsed?: boolean;
  collapsedHeader?: () => React.ReactNode;
}

// card chrome — overflow clip, white surface, soft lift on hover (refresh tokens)
const CARD = 'overflow-hidden bg-white rounded-md shadow-card transition-shadow duration-[400ms] hover:shadow-card-hover';
// header — fixed-height bar with a $grey inset hairline (no box shift) and top radius
const HEADER = 'panel-header h-[38px] bg-light rounded-t-md shadow-[inset_0_-1px_0_0_var(--color-grey)] flex items-center';
// content — fills below the 38px header, bottom radius
const CONTENT = 'panel-content h-[calc(100%-38px)] rounded-b-md';

/**
 * A panel component.
 *
 * Renders the chrome (border, header, etc) for the ModelExplorer, EDG, OET and
 * Inspector components (should be passed as react children).
 *
 */
const Panel = ({className, type, header, children, collapsed, collapsedHeader}: PanelProps) => {
  // `panel` + `panel-${type}` literals kept: Project/Inspector/ModelExplorer/OET
  // SCSS still target them (and `panel-collapsed`, `panel-content`) via descendant selectors.
  const allClassNames = ['panel', `panel-${type}`, CARD, className];
  return !collapsed ? (
    <div className={allClassNames.join(' ')}>
      <div className={HEADER}>{ header() }</div>
      <div className={CONTENT}>
        { children }
      </div>
    </div>
  ) : (
    <div className={allClassNames.concat(['panel-collapsed']).join(' ')}>
      { collapsedHeader!() }
    </div>
  )
};

export default Panel;
